import { cx } from "@/utils/cx";

/**
 * The Gymkee mark, from `identity-2027/logo/kit/icon-orange.svg`.
 *
 * Path for path from the kit on its own 1024 viewBox, so the optical centre is the kit's and
 * nothing is redrawn or re-proportioned. Two paints and only two: the rounded square is the
 * BRAND orange and not the action token: a product mark carries the brand ramp on every runtime
 * and never the colour of whatever a screen's primary happens to be (rule 337, and a personalised
 * surface rewrites the action tokens under it). The smile inside it is the paper
 * mark, which is the one pairing rule 17.3 allows. No hex is typed here; the tokens carry both.
 *
 * The smile takes `--gk-text-on-ink` rather than `--gk-surface-paper`, because paper flips to
 * ink under [data-theme="dark"] and the mark does not flip with it: `text-on-ink` holds the same
 * paper value in both blocks, which is exactly the paper the kit drew.
 */

export interface MarkProps {
  /** 32 in the Gymkee Pay bar: the smallest tile whose paper mark clears the kit's 16 minimum. */
  size?: number;
  className?: string;
}

export function Mark({ size = 32, className }: MarkProps) {
  return (
    <svg
      className={cx("flex-none", className)}
      width={size}
      height={size}
      viewBox="0 0 1024 1024"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="1024" height="1024" rx="229" fill="var(--gk-orange-500)" />
      <g transform="translate(292 102) scale(4.846387)">
        <path
          fill="var(--gk-text-on-ink)"
          fillRule="evenodd"
          d="M74.70 38.96 C82.74 48.74 87.14 61.01 87.17 73.68 C87.12 80.96 85.09 88.10 81.30 94.32 C79.88 96.60 78.20 98.70 76.31 100.60 C68.68 107.74 58.73 111.88 48.29 112.24 C33.15 112.73 20.19 108.45 10.34 95.88 C-4.36 77.11 1.08 40.92 28.87 32.14 C29.50 32.14 30.16 31.69 31.19 31.42 C36.22 29.97 41.45 29.29 46.69 29.42 C66.29 29.68 76.08 29.82 77.15 33.25 C77.42 34.32 77.24 36.10 74.70 38.96 Z M23.30 70.16 C23.37 75.68 25.63 80.95 29.58 84.81 C33.53 88.68 38.85 90.81 44.37 90.75 C47.15 90.93 49.94 90.54 52.56 89.61 C55.19 88.68 57.60 87.23 59.65 85.35 C61.70 83.46 63.35 81.18 64.50 78.65 C65.65 76.11 66.27 73.36 66.34 70.58 C66.40 67.79 65.90 65.02 64.87 62.43 C63.83 59.85 62.29 57.50 60.32 55.52 C58.36 53.54 56.02 51.99 53.44 50.94 C50.86 49.89 48.09 49.38 45.31 49.43 C42.46 49.25 39.61 49.66 36.93 50.64 C34.25 51.61 31.80 53.12 29.72 55.08 C27.65 57.04 26.01 59.41 24.89 62.03 C23.77 64.66 23.20 67.48 23.22 70.34 L23.30 70.16 Z"
        />
        <path
          fill="var(--gk-text-on-ink)"
          fillRule="evenodd"
          d="M58.36 126.59 C63.92 124.96 69.16 122.40 73.86 119.01 C80.32 114.28 82.41 110.09 83.35 110.72 C84.28 111.34 82.19 126.36 71.72 135.86 C55.33 150.79 31.06 142.94 29.28 142.32 C17.52 138.35 8.16 129.66 9.28 127.21 C10.39 124.76 20.77 129.75 39.61 129.13 C45.94 129.11 52.25 128.26 58.36 126.59 Z"
        />
      </g>
    </svg>
  );
}
